import { useInfiniteQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";

import { LoadMore } from "../components/LoadMore";
import { Row } from "../components/Row";
import { Wordmark } from "../components/Wordmark";
import { api, type Capture } from "../lib/api";
import { Icon } from "../lib/icons";

// The tax year of a receipt: payload.year, else the `tax-<year>` tag, else
// "Undated" (a vision misread with no legible year still has a home).
function taxYear(c: Capture): string {
  const y = c.payload?.year;
  if (typeof y === "number") return String(y);
  const tag = (c.tags ?? []).find((t) => /^tax-\d{4}$/.test(t));
  return tag ? tag.slice(4) : "Undated";
}

// Tax records are a silo: excluded from the main timeline / search / Ask, and
// surfaced only here by asking for them explicitly (category=tax). Grouped by
// year — the spine of the "what do I have for 2024?" payoff.
export function Tax() {
  const navigate = useNavigate();
  const q = useInfiniteQuery({
    queryKey: ["captures", { category: "tax" }],
    queryFn: ({ pageParam }) => api.listCaptures({ category: "tax", before_id: pageParam }),
    initialPageParam: undefined as number | undefined,
    getNextPageParam: (last) => last.next_before_id ?? undefined,
  });
  const items = useMemo(() => q.data?.pages.flatMap((p) => p.items) ?? [], [q.data]);

  const years = useMemo(() => {
    const byYear = new Map<string, Capture[]>();
    for (const c of items) {
      const y = taxYear(c);
      const list = byYear.get(y) ?? [];
      list.push(c);
      byYear.set(y, list);
    }
    // Newest year first; "Undated" sinks to the bottom.
    return [...byYear.entries()].sort(([a], [b]) => b.localeCompare(a));
  }, [items]);

  return (
    <>
      <header className="header">
        <div className="col">
          <div className="header-inner">
            <button className="back-link" onClick={() => navigate("/")}>
              <Icon.back /> timeline
            </button>
            <div style={{ flex: 1 }} />
            <Wordmark />
          </div>
        </div>
      </header>
      <main className="col" style={{ paddingTop: 24, paddingBottom: 96 }}>
        <div className="date-marker">tax</div>
        {q.isLoading ? (
          <p style={{ color: "var(--text-muted)", fontSize: 13 }}>Loading…</p>
        ) : items.length === 0 ? (
          q.isError ? null : (
            <p style={{ color: "var(--text-muted)", fontSize: 13 }}>
              No tax receipts yet. Send a bill photo captioned “für die Steuer”.
            </p>
          )
        ) : (
          years.map(([year, receipts]) => (
            <section className="tax-year" key={year}>
              <div className="tax-year-head">
                <h2>{year}</h2>
                <span>
                  {receipts.length} {receipts.length === 1 ? "receipt" : "receipts"}
                </span>
              </div>
              <ul className="row-list">
                {receipts.map((item) => (
                  <Row key={item.id} item={item} query="" />
                ))}
              </ul>
            </section>
          ))
        )}
        <LoadMore
          hasNextPage={q.hasNextPage}
          isFetchingNextPage={q.isFetchingNextPage}
          isError={q.isError}
          fetchNextPage={q.fetchNextPage}
        />
      </main>
    </>
  );
}
